import { Button, ButtonGroup, capitalize, Chip, Container, FormControl, Grid, InputLabel, MenuItem, Paper, Select, Snackbar, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField } from "@mui/material";
import { EkasiServeAppBar, formatCurrency, LoadingPage, SetState } from "./Common";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { useCallback, useEffect, useState } from "react";
import { Client } from "./Clients";
import { Company } from "./Companies";
import { BarChart, LineChart } from "@mui/x-charts";

export interface SearchParams {
    date_field: string;
	after_date: string;
	before_date: string;
	include_paid: number;
	include_unpaid: number;
	clients: Array<number>;
	products: Array<string>;
    business_units: Array<string>;
	group_by_date: string;
	group_by_item: string;
}

export interface SearchResult {
    name: string;
    date: string;
    total: number;
}

export function ExploreForm({ params, setParams, companies, clients }: { params: Partial<SearchParams>, setParams: SetState<Partial<SearchParams>>, companies: Array<Company>, clients: Array<Client> }) {
    const [product, setProduct] = useState("");
    const handleProductEnter = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.key === "Enter" && product !== "" && !(params.products?.includes(product) === true)) {
            setParams(params => ({ ...params, products: [ ...(params.products || []), product] }))
            setProduct("");
        }
    }, [params.products, product, setParams]);

    const [unit, setUnit] = useState("");
    const handleUnitEnter = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.key === "Enter" && unit !== "" && !(params.business_units?.includes(unit) === true)) {
            setParams(params => ({ ...params, business_units: [ ...(params.business_units || []), unit] }))
            setUnit("");
        }
    }, [params.business_units, unit, setParams]);

    return (
        <Grid container spacing={2} sx={{ alignItems: "center" }}>
            <Grid size={4}>
                <FormControl fullWidth>
                    <InputLabel>Date Field</InputLabel>
                    <Select
                        label="Date Field"
                        value={params.date_field || "quote"}
                        onChange={(ev) => setParams(params => ({ ...params, date_field: ev.target.value }))}>
                        <MenuItem value="quote">Quote Date</MenuItem>
                        <MenuItem value="po">PO Date</MenuItem>
                        <MenuItem value="tax">Tax Date</MenuItem>
                    </Select>
                </FormControl>
            </Grid>
            <Grid size={4}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                        label="From"
                        slotProps={{ textField: { fullWidth: true } }}
                        onAccept={(value) => setParams(params => ({ ...params, after_date: value?.format("YYYY-MM-DD") }))}
                        value={params.after_date === undefined || params.after_date === "" ? dayjs().subtract(1, 'year') : dayjs(params.after_date, "YYYY-MM-DD")} />
                </LocalizationProvider>
            </Grid>
            <Grid size={4}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                        label="To"
                        slotProps={{ textField: { fullWidth: true } }}
                        onAccept={(value) => setParams(params => ({ ...params, before_date: value?.format("YYYY-MM-DD") }))}
                        value={params.before_date === undefined || params.before_date === "" ? dayjs().add(1, 'month') : dayjs(params.before_date, "YYYY-MM-DD")} />
                </LocalizationProvider>
            </Grid>
            <Grid size={12}>
                <ButtonGroup fullWidth>
                    <Button 
                        variant={params.include_unpaid === 1 && params.include_paid === 0 ? 'contained' : 'outlined'}
                        onClick={() => setParams(params => ({ ...params, include_unpaid: 1, include_paid: 0}))}
                    >
                        Include Unpaid
                    </Button>
                    <Button 
                        variant={params.include_paid === 1 && params.include_unpaid === 0 ? 'contained' : 'outlined'}
                        onClick={() => setParams(params => ({ ...params, include_paid: 1, include_unpaid: 0 }))}
                    >
                        Include Paid
                    </Button>
                    <Button 
                        variant={params.include_paid === 1 && params.include_unpaid === 1 ? 'contained' : 'outlined'}
                        onClick={() => setParams(params => ({ ...params, include_paid: 1, include_unpaid: 1 }))}
                    >
                        Include Both
                    </Button>
                </ButtonGroup>
            </Grid>
            <Grid size={6}>
                <FormControl fullWidth>
                    <InputLabel>Clients</InputLabel>
                    <Select
                        label="Clients"
                        multiple
                        fullWidth
                        value={params.clients || []}
                        onChange={(ev) => setParams(params => ({ ...params, clients: typeof ev.target.value === "string" ? [] : ev.target.value }))}>
                        {
                        clients.map(client => (<MenuItem value={client.id}>{companies.find(company => company.id === client.company_id)?.name || ""} - {client.name}</MenuItem>))
                        }
                    </Select>
                </FormControl>
            </Grid>
            <Grid size={6}>
                <TextField
                    label="Business Units"
                    fullWidth
                    onKeyUp={handleUnitEnter}
                    value={unit}
                    onChange={(e) => setUnit(e.target.value)}
                    slotProps={{ input: {
                        startAdornment:
                        params.business_units?.length === 0 ? null : (
                            <div className="flex pt-3 gap-x-2">
                                {params.business_units?.map((item) => {
                                    return (
                                    <Chip
                                        key={item}
                                        label={item}
                                        variant="outlined"
                                        onDelete={() => {
                                            setParams(params => ({ ...params, business_units: params.business_units?.filter(v => v !== item) || [] }));
                                        }}
                                    />
                                    );
                                }) || []}
                            </div>
                        ),
                        // customize the style within the TextField
                        style: params.business_units?.length === 0 ? {} : {
                            display: "grid",
                            gridTemplateColumns: "1fr",
                            gridTemplateRows: "1fr 56px",
                        },
                    }
                }}/>
            </Grid>
            <Grid size={12}>
                <TextField
                    label="Products"
                    fullWidth
                    onKeyUp={handleProductEnter}
                    value={product}
                    onChange={(e) => setProduct(e.target.value)}
                    slotProps={{ input: {
                        startAdornment:
                        params.products?.length === 0 ? null : (
                            <div className="flex pt-3 gap-x-2">
                                {params.products?.map((item) => {
                                    return (
                                    <Chip
                                        key={item}
                                        label={item}
                                        variant="outlined"
                                        onDelete={() => {
                                            setParams(params => ({ ...params, products: params.products?.filter(v => v !== item) || [] }));
                                        }}
                                    />
                                    );
                                }) || []}
                            </div>
                        ),
                        // customize the style within the TextField
                        style: 
                        params.products?.length === 0
                            ? {}
                            : {
                                display: "grid",
                                gridTemplateColumns: "1fr",
                                gridTemplateRows: "1fr 56px",
                            },
                        }
                    }
                }/>
            </Grid>
            <Grid size={6}>
                <FormControl fullWidth>
                    <InputLabel>Group By Date</InputLabel>
                    <Select
                        label="Group By Date"
                        fullWidth
                        value={params.group_by_date || "month"}
                        onChange={(ev) => setParams(params => ({ ...params, group_by_date: ev.target.value }))}>
                        <MenuItem value="year">Year</MenuItem>
                        <MenuItem value="month">Month</MenuItem>
                        <MenuItem value="day">Day</MenuItem>
                        <MenuItem value="none">None</MenuItem>
                    </Select>
                </FormControl>
            </ Grid>
            <Grid size={6}>
                <FormControl fullWidth>
                    <InputLabel>Group By Item</InputLabel>
                    <Select
                        label="Group By Item"
                        fullWidth
                        value={params.group_by_item || "clients"}
                        onChange={(ev) => setParams(params => ({ ...params, group_by_item: ev.target.value }))}>
                        <MenuItem value="clients">Clients</MenuItem>
                        <MenuItem value="quotes">Quotes</MenuItem>
                        <MenuItem value="products">Products</MenuItem>
                        <MenuItem value="products_per_client">Products Per Client</MenuItem>
                        <MenuItem value="products_per_person">Products Per Person</MenuItem>
                        <MenuItem value="products_per_quote">Products Per Quote</MenuItem>
                        <MenuItem value="products_per_delivery">Products Per Delivery</MenuItem>
                    </Select>
                </FormControl>
            </Grid>
        </Grid>
    )
}

export function ExploreTable({ results, searchParams }: { results: Array<SearchResult>, searchParams: Partial<SearchParams> }) {
    return (
        <Grid container spacing={2} sx={{ alignItems: "center" }}>
            <Grid size={12}>
                <Paper>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell width="40%">Name</TableCell>
                                    {!isDataSingleSeries(searchParams) && <TableCell width="20%">Date</TableCell>}
                                    <TableCell width="20%">Total</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {results.map((row) => (
                                    <TableRow key={row.name + " " + row.date + " " + row.total}>
                                        <TableCell>{row.name}</TableCell>
                                        {!isDataSingleSeries(searchParams) && <TableCell>{row.date}</TableCell>}
                                        <TableCell>{formatCurrency(row.total)}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>

            </Grid>
        </Grid>
    )
}

function valueFormatter(value: number | null) {
    return formatCurrency(value || 0);
}

function isDataSingleSeries(params: Partial<SearchParams>): boolean {
    return params.group_by_date === "none" || params.group_by_item === "quotes";
}

function buildGraphDataset(results: Array<SearchResult>, params: Partial<SearchParams>): { useBar: boolean, dateFormat: string, fields: string[], dataset: any[] } {
    if (isDataSingleSeries(params)) {
        return { useBar: true, dateFormat: "", fields: ["total"], dataset: results.map(r => ({...r})) };
    }
    var dataset: any[] = [];
    var fields: string[] = [];
    var dateFormatInput = "";
    var dateFormatOutput = "";
    switch (params.group_by_date) {
    case "year": dateFormatInput = "YYYY"; dateFormatOutput = "YYYY"; break;
    case "month": dateFormatInput = "YYYY-MM"; dateFormatOutput = "YYYY MMM"; break;
    case "day": dateFormatInput = "YYYY-MM-DD"; dateFormatOutput = "MMM DD"; break;
    }
    var dateIter = dayjs(params.after_date, dateFormatInput);
    const finalDate = dayjs(params.before_date, dateFormatInput)
    while (dateIter.isBefore(finalDate) || dateIter.isSame(finalDate)) {
        var obj: any = { name: dateIter.valueOf() };
        for (let res of results) {
            if (res.date === dateIter.format(dateFormatInput)) {
                obj[res.name] = res.total;
                if (!fields.includes(res.name)) {
                    fields.push(res.name);
                }
            }
        }
        dataset.push(obj);
        dateIter = dateIter.add(1, params.group_by_date as dayjs.ManipulateType);
    }

    for (let idx in dataset) {
        for (let f of fields) {
            if (dataset[idx][f] === undefined) {
                dataset[idx][f] = 0;
            }
        }
    }
    
    return { useBar: false, dateFormat: dateFormatOutput, fields, dataset };
}

export function ExploreGraph({ results, searchParams }: { results: Array<SearchResult>, searchParams: Partial<SearchParams> }) {
    const { useBar, dateFormat, fields, dataset } = buildGraphDataset(results, searchParams);
    const yHeight = 120;
    return (
        <Grid container spacing={2} sx={{ alignItems: "center" }}>
            <Grid size={12}>
                <Paper>
                    {
                    useBar
                    ? <BarChart 
                        dataset={dataset}
                        xAxis={[{dataKey: "name", height: yHeight, tickLabelStyle: { angle: -45 } }]}
                        series={[{ dataKey: "total", label: capitalize(searchParams.group_by_item || ""), valueFormatter }]}
                        layout="vertical"
                        yAxis={[
                            {
                                label: 'Total',
                                width: 120,
                                valueFormatter
                            },
                        ]}
                        height={window.innerHeight - yHeight}/>
                    : <LineChart 
                        dataset={dataset}
                        xAxis={[{dataKey: "name", height: yHeight, tickLabelStyle: {angle: -45}, valueFormatter: (date: number) => dayjs(date).format(dateFormat)}]}
                        series={fields.map(field => ({ dataKey: field, label: field, valueFormatter, showMark: false }))}
                        yAxis={[
                            {
                                label: 'Total',
                                width: 120,
                                valueFormatter
                            },
                        ]}
                        height={window.innerHeight - yHeight}/>
                    }
                </Paper>
            </Grid>
        </Grid>
    )
}

export type SearchState = "editing" | "list_found" | "graph_found" | "empty";

export const ExplorePage = () => {
    const [results, setResults] = useState<Array<SearchResult>>([]);
    const [clients, setClients] = useState<Array<Client>>([]);
    const [companies, setCompanies] = useState<Array<Company>>([]);
    const [isLoading, setIsLoading] = useState(0);
    const [searchParams, setSearchParams] = useState<Partial<SearchParams>>({ 
        after_date: dayjs().startOf("year").format('YYYY-MM-DD'),  
        before_date: dayjs().add(1, 'year').startOf("year").format('YYYY-MM-DD'),  
        include_unpaid: 1, include_paid: 0, date_field: "quote", group_by_date: "month", group_by_item: "clients" });
    const [savedSearchParams, setSavedSearchParams] = useState<Partial<SearchParams>>(searchParams);
    const [searchState, setSearchState] = useState<SearchState>("editing");
    const [snackbar, setSnackbar] = useState("");

    useEffect(() => {
        const fetchClients = async () => {
            const companiesResponse = await fetch("/api/v1/companies");
            const companiesData = await companiesResponse.json();
            setCompanies(companiesData.result || []);
            if (companiesData.error) {
                setSnackbar(companiesData.error);
            }

            const clientsResponse = await fetch("/api/v1/clients?company_id=0");
            const clientsData = await clientsResponse.json();
            setClients(clientsData.result || []);
            if (clientsData.error) {
                setSnackbar(clientsData.error);
            }
            setIsLoading(lock => lock - 1);
        };
        setIsLoading(lock => lock + 1);
        fetchClients();
    }, []);

    const findQuotes = async () => {
        const response = await fetch(`/api/v1/explore/search`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(searchParams)
        });
        const data = await response.json();
        setSavedSearchParams(searchParams);
        if (data.error) {
            setSnackbar(data.error);
        } else if (data.result && data.result.length !== 0) {
            setResults(data.result);
            setSearchState("list_found");
        } else {
            setResults([]);
            setSearchState("empty");
        }
    }

    const graphQuotes = async () => {
        const response = await fetch(`/api/v1/explore/search`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(searchParams)
        });
        const data = await response.json();
        setSavedSearchParams(searchParams);
        if (data.error) {
            setSnackbar(data.error);
        } else if (data.result && data.result.length !== 0) {
            setResults(data.result);
            setSearchState("graph_found");
        } else {
            setResults([]);
            setSearchState("empty");
        }
    };

    if (isLoading) return (<LoadingPage />);

    return (
        <Stack spacing={0}>
            <EkasiServeAppBar />
            <Grid> <h1>Explore</h1> </Grid>
            <Grid component={Container} container spacing={2} sx={{ alignItems: "center" }}>
                <ExploreForm params={searchParams} setParams={setSearchParams} companies={companies} clients={clients} />
                <Grid size={5}></Grid>
                <Grid size={1}>
                    <Button fullWidth variant="contained" onClick={() => findQuotes()}>Search</Button>
                </Grid>
                <Grid size={1}>
                    <Button fullWidth variant="contained" onClick={() => graphQuotes()}>Graph</Button>
                </Grid>
                <Grid size={5}></Grid>
                <Grid size={12}>
                    {
                        (() => {
                            switch (searchState) {
                                case "editing": return <></>;
                                case "empty": return <h2> Nothing Found </h2>;
                                case "graph_found": return <ExploreGraph results={results} searchParams={savedSearchParams} />;
                                case "list_found": return <ExploreTable results={results} searchParams={savedSearchParams}/>;
                            }
                        })()
                    }
                </Grid>
            </Grid>
            <Snackbar
                open={snackbar !== ""}
                autoHideDuration={6000}
                onClose={(ev, reason) => setSnackbar("")}
                message={snackbar}/>
        </Stack>
    )
}